'use client';

import { AnonymousOrderCreationResponse, OrderDetails } from '@/types/gearApi/Order';
import { Price } from '@/types/Price';
import { mapToGTMItem } from '@/utils/helpers/gtmHelper';
import { sendGTMEvent } from '@next/third-parties/google';

interface AnalyticsServiceConfig {
	brandName?: string;
}

export class Analytics {
	private readonly brandName?: string;

	constructor(config: AnalyticsServiceConfig) {
		this.brandName = config.brandName;
	}

	trackAddToCart({
		sku,
		name,
		price,
		quantity,
		isLoggedIn,
		sharedCartId,
	}: {
		sku: string;
		name: string;
		price?: Price;
		quantity: number;
		isLoggedIn: boolean;
		sharedCartId: string | null;
	}) {
		sendGTMEvent({
			event: 'add_to_cart',
			is_logged_in: isLoggedIn,
			ecommerce: {
				currency: price?.currencyCode,
				value: price && (price.value / 100) * quantity,
				items: [
					mapToGTMItem({
						sku,
						name,
						price,
						quantity,
						...(this.brandName && { brandName: this.brandName }),
					}),
				],
			},
			...(sharedCartId && { shared_cart_id: sharedCartId }),
		});
	}

	trackAddPaymentInfo(
		{
			id,
			totalGross,
			totalNet,
			shippingDetails,
			orderLines,
			sharedCartId,
		}: OrderDetails | AnonymousOrderCreationResponse,
		paymentType: string,
		isLoggedIn: boolean,
	) {
		sendGTMEvent({
			event: 'add_payment_info',
			is_logged_in: isLoggedIn,
			ecommerce: {
				transaction_id: id,
				payment_type: paymentType,
				// Sum of (price * quantity) for all items.
				value: totalNet.value / 100,
				tax: (totalGross.value - totalNet.value) / 100,
				shipping: shippingDetails.shippingPrice.value / 100,
				currency: totalNet.currencyCode,
				items: orderLines?.map(({ sku, name, price, quantity }) =>
					mapToGTMItem({ sku, name, price, quantity, ...(this.brandName && { brandName: this.brandName }) }),
				),
			},
			...(sharedCartId && { shared_cart_id: sharedCartId }),
		});
	}
}
